import React, { useState } from 'react';
import BopBackLink from '../../../../components/BopBackLink';
import BopRowActions, { RowIcon } from '../../../../components/BopRowActions';
import { formatCurrency, formatNumber } from '../../../../utils/formatters';
import {
  BADGE, COMANDI, CONFIGURAZIONE_LETTA, DOMANI, OGGI, SEDI, STATO_COMANDO, TARIFFE, TIPO_COMANDO,
  Badge, Colonnina, Comando, Connettore, Tariffa, TipoComando, VoceConfigurazione,
  formatData, formatDataOra, whInKwh,
} from './dati';
import { Campo, StatoColonnina, StatoPresa } from './componenti';

interface Props {
  colonnina: Colonnina;
  gatewayOk: boolean;
  onAggiorna: (colonnina: Colonnina) => void;
  onIndietro: () => void;
}

type Parametri = Comando['parametri'];

interface Conferma {
  tipo: TipoComando;
  parametri: Parametri;
  titolo: string;
  testo: string;
}

interface FormModifica {
  nome: string;
  sedeId: string;
  attiva: boolean;
}

const URL_GATEWAY = 'wss://ocpp.pianinvest.it';

// Nel mockup la password nasce nel browser; nella pagina vera la genera il backend
function passwordCasuale(): string {
  const alfabeto = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  return Array.from({ length: 20 }, () => alfabeto[Math.floor(Math.random() * alfabeto.length)]).join('');
}

function statoTariffa(t: Tariffa, inVigore: Tariffa | null): { label: string; badge: string } {
  if (t.validaDal > OGGI) return { label: 'Futura', badge: 'bop-badge--info' };
  if (inVigore && t.id === inVigore.id) return { label: 'In vigore', badge: 'bop-badge--success' };
  return { label: 'Superata', badge: 'bop-badge--neutral' };
}

function badgeValidoOggi(b: Badge): boolean {
  return b.attivo && b.validoDal <= OGGI && (!b.validoAl || b.validoAl >= OGGI);
}

// Descrizione leggibile dei parametri di un comando, per lo storico
function dettaglioComando(c: Comando): string {
  const p = c.parametri;
  switch (c.tipo) {
    case 'AVVIO_REMOTO': return `Presa ${p?.connettore ?? '—'} · badge ${p?.codiceBadge ?? '—'}`;
    case 'STOP_REMOTO': return `Ricarica n. ${p?.transactionId ?? '—'}`;
    case 'RESET': return p?.tipo === 'Hard' ? 'Completo' : 'Normale';
    case 'SBLOCCO': return `Presa ${p?.connettore ?? '—'}`;
    case 'DISPONIBILITA': return p?.operativa ? 'Rimessa in servizio' : 'Messa fuori servizio';
    default: return '—';
  }
}

export default function VistaSchedaColonnina({ colonnina, gatewayOk, onAggiorna, onIndietro }: Props) {
  const [comandi, setComandi] = useState<Comando[]>(COMANDI[colonnina.id] ?? []);
  const [tariffe, setTariffe] = useState<Tariffa[]>(TARIFFE[colonnina.societaId] ?? []);
  const [fuoriServizio, setFuoriServizio] = useState(false);
  const [conferma, setConferma] = useState<Conferma | null>(null);
  const [avvio, setAvvio] = useState<Connettore | null>(null);
  const [badgeScelto, setBadgeScelto] = useState('');
  const [password, setPassword] = useState<{ valore: string; rigenerata: boolean } | null>(null);
  const [configurazione, setConfigurazione] = useState<VoceConfigurazione[] | null>(null);
  const [formModifica, setFormModifica] = useState<FormModifica | null>(null);
  const [erroreModifica, setErroreModifica] = useState('');
  const [formTariffa, setFormTariffa] = useState<{ prezzo: string; validaDal: string } | null>(null);
  const [erroreTariffa, setErroreTariffa] = useState('');

  // Fuori servizio: le prese risultano "Fuori servizio" finché non si rimette in servizio
  const prese = fuoriServizio
    ? colonnina.connettori.map((p): Connettore => ({ ...p, stato: 'Unavailable' }))
    : colonnina.connettori;

  // Nel mockup i badge sono tutti dipendenti di Pianinvest (società 1): l'avvio da remoto
  // ammette solo badge validi oggi di dipendenti della società della colonnina
  const badgeAvviabili = colonnina.societaId === 1 ? BADGE.filter(badgeValidoOggi) : [];

  const tariffaInVigore = tariffe
    .filter((t) => t.validaDal <= OGGI)
    .sort((a, b) => b.validaDal.localeCompare(a.validaDal))[0] ?? null;

  // Esito simulato: senza gateway il comando scade (nella realtà dopo 2 minuti)
  const esitoSimulato = (tipo: TipoComando): Pick<Comando, 'stato' | 'risposta' | 'messaggio'> => {
    if (!gatewayOk) return { stato: 'SCADUTO', risposta: null, messaggio: 'Il gateway non ha preso il comando entro 2 minuti' };
    if (!colonnina.collegata) return { stato: 'ERRORE', risposta: null, messaggio: 'Colonnina non collegata' };
    if (tipo === 'LEGGI_CONFIGURAZIONE') return { stato: 'ACCETTATO', risposta: CONFIGURAZIONE_LETTA, messaggio: null };
    return { stato: 'ACCETTATO', risposta: { status: 'Accepted' }, messaggio: null };
  };

  const invia = (tipo: TipoComando, parametri: Parametri) => {
    const id = Date.now();
    const nuovo: Comando = {
      id, tipo, parametri, stato: 'IN_ATTESA', risposta: null, messaggio: null,
      richiestoDa: 'Paolo', createdAt: new Date().toISOString(), esitoAt: null,
    };
    setComandi((prev) => [nuovo, ...prev]);
    const esito = esitoSimulato(tipo);
    // Nel mockup l'esito arriva dopo due secondi; nella pagina vera quando il gateway lo riporta
    window.setTimeout(() => {
      setComandi((prev) => prev.map((c): Comando => (
        c.id !== id ? c : { ...c, ...esito, esitoAt: new Date().toISOString() }
      )));
      // La disponibilità cambia solo se la colonnina ha accettato il comando
      if (tipo === 'DISPONIBILITA' && esito.stato === 'ACCETTATO') {
        setFuoriServizio(parametri?.operativa === false);
      }
    }, 2000);
    setConferma(null);
    setAvvio(null);
  };

  const generaPassword = () => {
    setPassword({ valore: passwordCasuale(), rigenerata: colonnina.passwordImpostata });
    if (!colonnina.passwordImpostata) onAggiorna({ ...colonnina, passwordImpostata: true });
  };

  const apriModifica = () => {
    setErroreModifica('');
    setFormModifica({ nome: colonnina.nome, sedeId: colonnina.sede ? String(colonnina.sede.id) : '', attiva: colonnina.attiva });
  };

  const salvaModifica = () => {
    if (!formModifica) return;
    if (!formModifica.nome.trim()) { setErroreModifica('Il nome è obbligatorio'); return; }
    onAggiorna({
      ...colonnina,
      nome: formModifica.nome.trim(),
      sede: SEDI.find((s) => s.id === Number(formModifica.sedeId)) ?? null,
      attiva: formModifica.attiva,
      // Disattivata: esce dalla configurazione del gateway e viene scollegata
      collegata: formModifica.attiva ? colonnina.collegata : false,
    });
    setFormModifica(null);
  };

  const salvaTariffa = () => {
    if (!formTariffa) return;
    const prezzo = Number(formTariffa.prezzo.replace(',', '.'));
    if (!(prezzo > 0)) { setErroreTariffa('Il prezzo deve essere maggiore di zero'); return; }
    if (formTariffa.validaDal < OGGI) { setErroreTariffa('La validità non può iniziare nel passato'); return; }
    if (tariffe.some((t) => t.validaDal === formTariffa.validaDal)) {
      setErroreTariffa('Esiste già una tariffa che parte da quel giorno');
      return;
    }
    const nuova: Tariffa = { id: Date.now(), prezzoKwh: prezzo, validaDal: formTariffa.validaDal, creatoDa: 'Paolo' };
    setTariffe([nuova, ...tariffe].sort((a, b) => b.validaDal.localeCompare(a.validaDal)));
    setFormTariffa(null);
  };

  const notaComandi = !colonnina.attiva
    ? 'I comandi sono disponibili solo per le colonnine attive.'
    : gatewayOk
      ? "I comandi passano dal gateway: l'esito compare qui sotto entro pochi secondi."
      : 'Il gateway non risponde: i comandi restano in attesa e scadono dopo 2 minuti.';

  return (
    <div className="bop-ricarica__stack">
      <BopBackLink onClick={onIndietro} label="Torna alle colonnine" />

      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2 className="bop-page-title">{colonnina.nome}</h2>
          <p className="bop-page-subtitle">{colonnina.societaNome} · {colonnina.sede?.nome ?? 'Nessuna sede'}</p>
        </div>
        <div className="bop-flex">
          <button className="bop-btn bop-btn-secondary" onClick={apriModifica}>Modifica</button>
          <button className="bop-btn bop-btn-secondary" onClick={generaPassword}>
            {colonnina.passwordImpostata ? 'Rigenera password' : 'Genera password'}
          </button>
        </div>
      </div>

      {!gatewayOk && (
        <div className="bop-alert bop-alert--danger bop-mb-0">
          Gateway non raggiungibile: lo stato qui sotto è l'ultimo noto e i comandi scadono senza arrivare alla colonnina.
        </div>
      )}
      {!colonnina.attiva && (
        <div className="bop-alert bop-alert--warning bop-mb-0">
          Colonnina disattivata: non è nella configurazione del gateway e non accetta comandi. Si riattiva da Modifica.
        </div>
      )}

      <div className="bop-card">
        <div className="bop-card__header"><h3 className="bop-card__title">Collegamento</h3></div>
        <div className="bop-card__body">
          <div className="bop-ricarica__campi">
            <Campo label="Stato"><StatoColonnina colonnina={colonnina} gatewayOk={gatewayOk} /></Campo>
            <Campo label="Ultimo contatto">{formatDataOra(colonnina.ultimoContattoAt)}</Campo>
            <Campo label="Indirizzo di collegamento">
              <span className="bop-text-mono">{URL_GATEWAY}/{colonnina.codiceOcpp}</span>
            </Campo>
            <Campo label="Marca e modello">
              {colonnina.marca ? `${colonnina.marca} ${colonnina.modello ?? ''}` : 'Non ancora collegata'}
            </Campo>
            <Campo label="Seriale">{colonnina.seriale ?? '—'}</Campo>
            <Campo label="Firmware">{colonnina.firmware ?? '—'}</Campo>
            <Campo label="Disponibilità">{fuoriServizio ? 'Fuori servizio' : 'In servizio'}</Campo>
          </div>
        </div>
      </div>

      <div className="bop-card">
        <div className="bop-card__header"><h3 className="bop-card__title">Prese</h3></div>
        {prese.length === 0 ? (
          <div className="bop-empty-state">
            <p className="bop-empty-state__text">Le prese compaiono quando la colonnina si collega la prima volta</p>
          </div>
        ) : (
          <table className="bop-table">
            <thead>
              <tr>
                <th>Presa</th>
                <th>Stato</th>
                <th>Ricarica in corso</th>
                <th>Errore</th>
                <th className="bop-table__th--actions">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {prese.map((p) => (
                <tr key={p.numero}>
                  <td>Presa {p.numero}</td>
                  <td><StatoPresa presa={p} gatewayOk={gatewayOk} /></td>
                  <td>
                    {p.sessioneInCorso ? (
                      <>
                        {p.sessioneInCorso.dipendente ?? 'Badge sconosciuto'}
                        <div className="bop-ricarica__secondaria">
                          dalle {formatDataOra(p.sessioneInCorso.inizioAt)} ·{' '}
                          {formatNumber(whInKwh(p.sessioneInCorso.energiaWh))} kWh
                          {gatewayOk ? '' : ' · ultimo dato noto'}
                        </div>
                      </>
                    ) : <span className="bop-text-muted">—</span>}
                  </td>
                  <td className="bop-text-muted">{p.codiceErrore ?? '—'}</td>
                  <td className="bop-table__td--actions">
                    <span className="bop-ricarica__comandi bop-ricarica__comandi--destra">
                      {(p.stato === 'Available' || p.stato === 'Preparing') && (
                        <button className="bop-btn bop-btn-secondary bop-btn-sm"
                          disabled={!gatewayOk || !colonnina.attiva}
                          onClick={() => { setBadgeScelto(''); setAvvio(p); }}>
                          Avvia ricarica
                        </button>
                      )}
                      {p.sessioneInCorso && (
                        <button className="bop-btn bop-btn-secondary bop-btn-sm"
                          disabled={!gatewayOk || !colonnina.attiva}
                          onClick={() => {
                            const s = p.sessioneInCorso;
                            if (!s) return;
                            setConferma({
                              tipo: 'STOP_REMOTO', parametri: { transactionId: s.transactionId },
                              titolo: 'Fermare la ricarica?',
                              testo: `La ricarica sulla presa ${p.numero} viene chiusa e conteggiata con l'energia erogata fin qui.`,
                            });
                          }}>
                          Ferma ricarica
                        </button>
                      )}
                      <button className="bop-btn bop-btn-ghost bop-btn-sm" disabled={!colonnina.attiva}
                        onClick={() => invia('SBLOCCO', { connettore: p.numero })}>
                        Sblocca
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bop-card">
        <div className="bop-card__header"><h3 className="bop-card__title">Comandi</h3></div>
        <div className="bop-card__body bop-ricarica__form">
          <div className="bop-ricarica__comandi">
            <button className="bop-btn bop-btn-secondary" disabled={!colonnina.attiva}
              onClick={() => invia('RICHIESTA_STATO', null)}>
              Aggiorna stato
            </button>
            <button className="bop-btn bop-btn-secondary" disabled={!colonnina.attiva}
              onClick={() => invia('LEGGI_CONFIGURAZIONE', null)}>
              Leggi configurazione
            </button>
            <button className="bop-btn bop-btn-secondary" disabled={!colonnina.attiva} onClick={() => setConferma({
              tipo: 'RESET', parametri: { tipo: 'Soft' }, titolo: 'Riavviare la colonnina?',
              testo: 'Le ricariche in corso vengono chiuse prima del riavvio.',
            })}>
              Riavvia
            </button>
            <button className="bop-btn bop-btn-secondary" disabled={!colonnina.attiva} onClick={() => setConferma({
              tipo: 'RESET', parametri: { tipo: 'Hard' }, titolo: 'Riavvio completo?',
              testo: 'La colonnina si spegne e si riaccende subito, anche con ricariche in corso. Usalo solo se il riavvio normale non risponde.',
            })}>
              Riavvio completo
            </button>
            <button className="bop-btn bop-btn-secondary" disabled={!colonnina.attiva} onClick={() => setConferma({
              tipo: 'DISPONIBILITA', parametri: { connettore: 0, operativa: fuoriServizio },
              titolo: fuoriServizio ? 'Rimettere in servizio?' : 'Mettere fuori servizio?',
              testo: fuoriServizio
                ? 'La colonnina torna disponibile per le ricariche.'
                : 'La colonnina non accetta nuove ricariche finché non la rimetti in servizio.',
            })}>
              {fuoriServizio ? 'Rimetti in servizio' : 'Metti fuori servizio'}
            </button>
          </div>
          <p className="bop-ricarica__nota">{notaComandi}</p>
        </div>
        <table className="bop-table">
          <thead>
            <tr>
              <th>Richiesto il</th>
              <th>Comando</th>
              <th>Dettaglio</th>
              <th>Richiesto da</th>
              <th>Esito</th>
              <th>Messaggio</th>
            </tr>
          </thead>
          <tbody>
            {comandi.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="bop-empty-state">
                    <p className="bop-empty-state__text">Nessun comando inviato a questa colonnina</p>
                  </div>
                </td>
              </tr>
            ) : comandi.map((c) => (
              <tr key={c.id}>
                <td>{formatDataOra(c.createdAt)}</td>
                <td>{TIPO_COMANDO[c.tipo]}</td>
                <td>
                  {c.tipo === 'LEGGI_CONFIGURAZIONE' && c.risposta ? (
                    <button className="bop-btn bop-btn-ghost bop-btn-sm"
                      onClick={() => setConfigurazione((c.risposta?.configurationKey ?? []) as VoceConfigurazione[])}>
                      Vedi configurazione
                    </button>
                  ) : dettaglioComando(c)}
                </td>
                <td>{c.richiestoDa}</td>
                <td><span className={`bop-badge ${STATO_COMANDO[c.stato].badge}`}>{STATO_COMANDO[c.stato].label}</span></td>
                <td className="bop-text-muted">{c.messaggio ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bop-card">
        <div className="bop-card__header">
          <h3 className="bop-card__title">Tariffe di {colonnina.societaNome}</h3>
          <button className="bop-btn bop-btn-primary bop-btn-sm"
            onClick={() => { setErroreTariffa(''); setFormTariffa({ prezzo: '', validaDal: DOMANI }); }}>
            + Nuova tariffa
          </button>
        </div>
        <div className="bop-card__body">
          <p className="bop-ricarica__nota">
            Valgono per tutte le colonnine della società. Ogni ricarica usa la tariffa in vigore il giorno in cui inizia.
          </p>
        </div>
        <table className="bop-table">
          <thead>
            <tr>
              <th>Valida dal</th>
              <th className="bop-text-right">Prezzo</th>
              <th>Inserita da</th>
              <th>Stato</th>
              <th className="bop-table__th--actions">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {tariffe.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <div className="bop-empty-state">
                    <p className="bop-empty-state__text">
                      Nessuna tariffa: le ricariche risultano anomale finché non ne inserisci una
                    </p>
                  </div>
                </td>
              </tr>
            ) : tariffe.map((t) => {
              const s = statoTariffa(t, tariffaInVigore);
              return (
                <tr key={t.id}>
                  <td>{formatData(t.validaDal)}</td>
                  <td className="bop-text-right">{formatCurrency(t.prezzoKwh)} €/kWh</td>
                  <td>{t.creatoDa}</td>
                  <td><span className={`bop-badge ${s.badge}`}>{s.label}</span></td>
                  <td className="bop-table__td--actions">
                    <BopRowActions actions={[
                      {
                        icon: <RowIcon.Trash />, label: 'Elimina', variant: 'danger', hidden: t.validaDal <= OGGI,
                        onClick: () => setTariffe(tariffe.filter((x) => x.id !== t.id)),
                      },
                    ]} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {conferma && (
        <div className="bop-modal-overlay" onClick={() => setConferma(null)}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">{conferma.titolo}</h2>
              <button className="bop-modal__close" onClick={() => setConferma(null)} aria-label="Chiudi">✕</button>
            </div>
            <div className="bop-modal__body">
              <p className="bop-ricarica__nota">{conferma.testo}</p>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setConferma(null)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={() => invia(conferma.tipo, conferma.parametri)}>
                Conferma
              </button>
            </div>
          </div>
        </div>
      )}

      {avvio && (
        <div className="bop-modal-overlay" onClick={() => setAvvio(null)}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">Avvia ricarica sulla presa {avvio.numero}</h2>
              <button className="bop-modal__close" onClick={() => setAvvio(null)} aria-label="Chiudi">✕</button>
            </div>
            <div className="bop-modal__body bop-ricarica__form">
              {badgeAvviabili.length === 0 ? (
                <p className="bop-ricarica__nota">
                  Nessun badge valido oggi fra i dipendenti di {colonnina.societaNome}.
                </p>
              ) : (
                <div className="bop-field">
                  <label className="bop-field__label" htmlFor="avvio-badge">
                    Badge <span className="bop-field__required">*</span>
                  </label>
                  <select id="avvio-badge" className="bop-field__select" value={badgeScelto}
                    onChange={(e) => setBadgeScelto(e.target.value)}>
                    <option value="">Scegli…</option>
                    {badgeAvviabili.map((b) => (
                      <option key={b.id} value={b.codice}>{b.dipendente.cognome} {b.dipendente.nome} · {b.codice}</option>
                    ))}
                  </select>
                  <p className="bop-field__hint">
                    La ricarica viene intestata al titolare del badge: si possono scegliere solo dipendenti della
                    società della colonnina.
                  </p>
                </div>
              )}
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setAvvio(null)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" disabled={!badgeScelto}
                onClick={() => invia('AVVIO_REMOTO', { connettore: avvio.numero, codiceBadge: badgeScelto })}>
                Avvia
              </button>
            </div>
          </div>
        </div>
      )}

      {password && (
        <div className="bop-modal-overlay">
          <div className="bop-modal">
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">Password della colonnina</h2>
            </div>
            <div className="bop-modal__body bop-ricarica__form">
              <div className="bop-alert bop-alert--warning bop-mb-0">
                Copiala adesso nella configurazione della colonnina: dopo la chiusura non sarà più visibile.
                {password.rigenerata
                  ? ' Da questo momento la password precedente non funziona più: la colonnina viene scollegata e si ricollega solo con questa.'
                  : ''}
              </div>
              <div className="bop-ricarica__campi">
                <Campo label="Indirizzo"><span className="bop-text-mono">{URL_GATEWAY}/{colonnina.codiceOcpp}</span></Campo>
                <Campo label="Utente"><span className="bop-text-mono">{colonnina.codiceOcpp}</span></Campo>
              </div>
              <div className="bop-ricarica__password bop-text-mono">{password.valore}</div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-primary" onClick={() => setPassword(null)}>Ho copiato la password</button>
            </div>
          </div>
        </div>
      )}

      {configurazione && (
        <div className="bop-modal-overlay" onClick={() => setConfigurazione(null)}>
          <div className="bop-modal bop-modal--lg" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">Configurazione letta</h2>
              <button className="bop-modal__close" onClick={() => setConfigurazione(null)} aria-label="Chiudi">✕</button>
            </div>
            <div className="bop-modal__body">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Parametro</th>
                    <th>Valore</th>
                    <th>Modificabile</th>
                  </tr>
                </thead>
                <tbody>
                  {configurazione.map((v) => (
                    <tr key={v.key}>
                      <td className="bop-text-mono">{v.key}</td>
                      <td className="bop-text-mono">{v.value ?? '—'}</td>
                      <td>{v.readonly ? 'No' : 'Sì'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setConfigurazione(null)}>Chiudi</button>
            </div>
          </div>
        </div>
      )}

      {formModifica && (
        <div className="bop-modal-overlay" onClick={() => setFormModifica(null)}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">Modifica colonnina</h2>
              <button className="bop-modal__close" onClick={() => setFormModifica(null)} aria-label="Chiudi">✕</button>
            </div>
            <div className="bop-modal__body bop-ricarica__form">
              {erroreModifica && <div className="bop-alert bop-alert--danger bop-mb-0">{erroreModifica}</div>}
              <div className="bop-field">
                <label className="bop-field__label" htmlFor="mod-nome">
                  Nome <span className="bop-field__required">*</span>
                </label>
                <input id="mod-nome" className="bop-field__input" value={formModifica.nome}
                  onChange={(e) => setFormModifica({ ...formModifica, nome: e.target.value })} />
              </div>
              <div className="bop-field">
                <label className="bop-field__label" htmlFor="mod-sede">Sede</label>
                <select id="mod-sede" className="bop-field__select" value={formModifica.sedeId}
                  onChange={(e) => setFormModifica({ ...formModifica, sedeId: e.target.value })}>
                  <option value="">Nessuna sede</option>
                  {SEDI.map((s) => (<option key={s.id} value={s.id}>{s.nome}</option>))}
                </select>
              </div>
              <label className="bop-flex">
                <input type="checkbox" checked={formModifica.attiva}
                  onChange={(e) => setFormModifica({ ...formModifica, attiva: e.target.checked })} />
                Colonnina attiva
              </label>
              <p className="bop-ricarica__nota">
                Il codice OCPP non si modifica. Una colonnina disattivata esce dalla configurazione del gateway: viene
                scollegata e non accetta né ricariche né comandi.
              </p>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setFormModifica(null)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salvaModifica}>Salva</button>
            </div>
          </div>
        </div>
      )}

      {formTariffa && (
        <div className="bop-modal-overlay" onClick={() => setFormTariffa(null)}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">Nuova tariffa</h2>
              <button className="bop-modal__close" onClick={() => setFormTariffa(null)} aria-label="Chiudi">✕</button>
            </div>
            <div className="bop-modal__body bop-ricarica__form">
              {erroreTariffa && <div className="bop-alert bop-alert--danger bop-mb-0">{erroreTariffa}</div>}
              <div className="bop-form-row">
                <div className="bop-field">
                  <label className="bop-field__label" htmlFor="tar-prezzo">
                    Prezzo €/kWh <span className="bop-field__required">*</span>
                  </label>
                  <input id="tar-prezzo" type="number" min="0" step="0.01" className="bop-field__input"
                    value={formTariffa.prezzo}
                    onChange={(e) => setFormTariffa({ ...formTariffa, prezzo: e.target.value })} />
                </div>
                <div className="bop-field">
                  <label className="bop-field__label" htmlFor="tar-dal">
                    Valida dal <span className="bop-field__required">*</span>
                  </label>
                  <input id="tar-dal" type="date" min={OGGI} className="bop-field__input"
                    value={formTariffa.validaDal}
                    onChange={(e) => setFormTariffa({ ...formTariffa, validaDal: e.target.value })} />
                </div>
              </div>
              <p className="bop-ricarica__nota">
                Le ricariche già fatte non cambiano: ognuna tiene la tariffa in vigore il giorno in cui è iniziata.
              </p>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setFormTariffa(null)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salvaTariffa}>Salva tariffa</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
